import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import '../styles/globals.css';
import '../styles/loading.css';
import '@zillow/constellation-fonts/zillow-fonts.css';
import '@zillow/constellation-fonts/legacy-zillow-fonts.css';
import { RpErrorBoundary, useBootstrap } from '@zg-rentals/rp-bootstrap-browser';
import { setClient as setRentalManagerApiBetaClient } from '@zg-rentals/ts-rental-manager-api-beta-client';
import { getClient } from '@zg-rentals/ts-client-http-client';
import { HeadBase } from '@zg-rentals/next-components';
import { ToastProvider } from '@zillow/constellation-10';
import { ErrorPage } from '../components/Error/ErrorPage';
import { BASE_URL, EXTERNAL_URLS } from '../constants/urls';
import { DefaultQueryProvider } from '@zg-rentals/react-query5';
import { Auth } from '../AuthProvider';

const NoSsrApp = dynamic(() => import('../components/App'), { ssr: false });

setRentalManagerApiBetaClient(getClient().extend({ prefixUrl: BASE_URL }));

const ApplicationsLandlordWeb = (appProps: AppProps) => {
  useBootstrap(() => import('../bootstrap/browser'));

  return (
    <>
      <HeadBase applicationName="applications-landlord-web" buildNumber={process.env.NEXT_PUBLIC_BUILD_NUMBER}>
        <title>Rental Applications | Zillow</title>
        <link rel="preconnect" href="//www.zillowstatic.com/" crossOrigin="" />
        <link
          rel="preload"
          href="//www.zillowstatic.com/s3/constellation-website/public/shared/fonts/open-sans/LATEST/open-sans-variable.woff2"
          as="font"
          type="font/woff2"
          crossOrigin=""
        />
        {/* Visual refresh fonts 
         https://constellation.pages.zgtools.net/constellation/main/?path=/docs/documentation-font-loading-getting-started--api#if-you-need-visual-refresh-theme-fonts 
         */}
      </HeadBase>

      <DefaultQueryProvider
        defaultOptions={{
          queries: {
            retry: false,
            refetchOnWindowFocus: false,
            staleTime: 1000 * 60 * 30, // 30 minutes
          },
        }}
        mutationErrorContext={() => undefined} // Note: disable serializing mutation variables
      >
        <Auth.Provider>
          <ToastProvider>
            <RpErrorBoundary
              fallback={
                <ErrorPage
                  errorCode={500}
                  errorMessage="Internal server error"
                  homePageText="Return to your properties dashboard"
                  homePageUrl={EXTERNAL_URLS.PROPERTIES}
                />
              }
            >
              <NoSsrApp appProps={appProps} />
            </RpErrorBoundary>
          </ToastProvider>
        </Auth.Provider>
      </DefaultQueryProvider>
    </>
  );
};

export default ApplicationsLandlordWeb;
